import { useCallback, useEffect, useState } from "react";
import axiosInstant from "../api";
import { getBrandOrder } from "../api/endpoints";
import { FilmWindow } from "../utils/filmWindow";

/**
 * Reads the estimated filming window + filming date for a project's SELECTION.
 *
 * They live on brand_order_pros, not on the project, so they come from the
 * order payload (the same shape useProcessSiblings consumes) rather than the
 * project GET. Every project sharing a selection therefore shows one window and
 * one filming date.
 *
 * Returns null while loading, when there is no selection, or on failure — every
 * caller treats "no window" as "not set yet", so a failed fetch degrades to the
 * same read-only state rather than throwing.
 */
export function useSelectionFilmWindow(
    orderId?: number | null,
    selectionId?: number | null
) {
    const [filmWindow, setFilmWindow] = useState<FilmWindow | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const refetch = useCallback(() => {
        if (!orderId || selectionId == null) {
            setFilmWindow(null);
            return undefined;
        }
        let active = true;
        setIsLoading(true);
        axiosInstant
            .get(getBrandOrder(Number(orderId)))
            .then((res) => {
                if (!active) return;
                const match = (res.data?.order_pros || []).find(
                    (op: any) => Number(op.id) === Number(selectionId)
                );
                setFilmWindow(match || null);
            })
            .catch(() => {
                if (active) setFilmWindow(null);
            })
            .finally(() => {
                if (active) setIsLoading(false);
            });
        return () => {
            active = false;
        };
    }, [orderId, selectionId]);

    useEffect(() => refetch(), [refetch]);

    return { filmWindow, setFilmWindow, isLoading, refetch };
}
